import React from 'react';
import { motion } from 'framer-motion';
import SectionHeading from '@/components/brand/SectionHeading';
import CountUp from '@/components/brand/CountUp';
import { fadeUp, staggerParent, viewportOnce } from '@/lib/motion';
import { HOME } from '@/constants/testIds';

const DEFAULT_STATS = [
  { value: 20,  suffix: '+', label: 'Years of Experience' },
  { value: 800, suffix: '+', label: 'Web Portals Delivered' },
  { value: 400, suffix: '+', label: 'Mobile Apps & Games' },
  { value: 35,  suffix: '+', label: 'In-House Experts' },
  { value: 50,  suffix: '+', label: 'AI Solutions Deployed' },
];

const DEFAULT_HEADING  = 'Company Achievements';
const DEFAULT_EYEBROW  = 'Our Track Record';
const DEFAULT_SUBHEAD  = 'Two decades of shipping software that works — and now leading the next wave with AI-first solutions built for real business outcomes.';

export default function Achievements({ data }) {
  const stats   = Array.isArray(data?.stats) && data.stats.length ? data.stats : DEFAULT_STATS;
  const heading = data?.heading  || DEFAULT_HEADING;
  const eyebrow = data?.eyebrow  || DEFAULT_EYEBROW;
  const subhead = data?.subhead  || DEFAULT_SUBHEAD;

  return (
    <section
      id="achievements"
      data-testid={HOME.achievementsRow}
      className="py-16 bg-white"
    >
      <div className="container mx-auto">

        <SectionHeading
          title={heading}
          eyebrow={eyebrow}
          subtitle={subhead}
          align="center"
          className="mb-14"
        />

        {/* ── Stats strip ── */}
        <motion.div
          variants={staggerParent(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5"
        >
          {stats.map((s) => (
            /* Outer div handles stagger entrance; inner handles hover variants */
            <motion.div key={s.label} variants={fadeUp}>
              <motion.div
                whileHover="hovered"
                initial="rest"
                animate="rest"
                variants={{
                  rest: {
                    y: 0,
                    scale: 1,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                    borderColor: 'rgba(226,232,240,1)',
                  },
                  hovered: {
                    y: -12,
                    scale: 1.06,
                    boxShadow: '0 28px 56px rgba(201,150,46,0.30), 0 0 0 2px rgba(201,150,46,1)',
                    borderColor: 'rgba(201,150,46,1)',
                  },
                }}
                style={{
                  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                  borderColor: 'rgba(226,232,240,1)',
                }}
                transition={{ type: 'spring', stiffness: 280, damping: 20 }}
                className="group/stat relative overflow-hidden bg-white border-2 rounded-2xl px-6 py-10 flex flex-col items-center text-center gap-2 cursor-default"
              >
                {/* Gold radial wash — appears on hover */}
                <motion.div
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  variants={{
                    rest:    { opacity: 0 },
                    hovered: { opacity: 1 },
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    background:
                      'radial-gradient(ellipse at center, rgba(232,194,90,0.14) 0%, transparent 68%)',
                  }}
                />

                {/* Animated gold number — scales up on hover */}
                <motion.div
                  className="relative text-4xl sm:text-5xl font-extrabold leading-none bg-gradient-to-br from-[#E8C25A] to-[#C9962E] bg-clip-text text-transparent"
                  variants={{
                    rest:    { scale: 1 },
                    hovered: { scale: 1.18 },
                  }}
                  transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                >
                  <CountUp to={s.value} suffix={s.suffix} />
                </motion.div>

                {/* Gold underline — widens on hover */}
                <div className="h-[2px] w-6 rounded-full bg-gradient-to-r from-transparent via-[#C9962E] to-transparent transition-all duration-300 group-hover/stat:w-16" />

                {/* Label — darkens on hover */}
                <motion.div
                  className="text-[11px] uppercase tracking-widest font-semibold"
                  variants={{
                    rest:    { color: '#64748b' },
                    hovered: { color: '#0D2347' },
                  }}
                  style={{ color: '#64748b' }}
                  transition={{ duration: 0.2 }}
                >
                  {s.label}
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
}
